import type { AdcellPaidSubscription } from '@/utils/adcell'

interface AdcellApi {
  Tracking: {
    /*
     * ADCELL's printed snippet calls track() without arguments, but trad.js accepts
     * both and only falls back to the bid/adcref URL parameters when they are empty.
     */
    track: (bid?: string, adcref?: string) => void
  }
}

declare global {
  interface Window {
    Adcell?: AdcellApi
  }
}

interface ProvidedAdcell {
  trackPaidSubscription: (subscription: AdcellPaidSubscription) => void
}

export default defineNuxtPlugin({
  name: 'adcell-tracking',
  dependsOn: ['cookiebot'],
  setup() {
    const { mode } = useRuntimeConfig().public
    const systemStore = useSystemStore()

    // TODO: Track only on production after testing is done
    const isTrackedEnvironment = mode === 'production' || mode === 'staging'

    if (!isTrackedEnvironment || systemStore.platform !== 'EU') {
      const disabled: ProvidedAdcell = { trackPaidSubscription: () => {} }

      return { provide: { adcell: disabled } }
    }

    const { $cookiebot } = useNuxtApp()
    const { logError } = useErrorHandling()

    const hasMarketingConsent = computed(() => {
      const cbInstance = $cookiebot.instance
      if (!cbInstance) return false

      return cbInstance.consented && cbInstance.consent.marketing
    })

    const trackingParams = readAdcellTrackingParams(window.location.search)
    if (trackingParams) parkAdcellTrackingParams(trackingParams)

    const { onLoaded, onError } = useScript(
      {
        src: 'https://t.adcell.com/js/trad.js',
        type: 'text/javascript',
        /* t.adcell.com sends no CORS headers, so the tag must not request the script in CORS mode. */
        crossorigin: false,
      },
      {
        trigger: hasMarketingConsent,
        use() {
          return window.Adcell
        },
      }
    )

    onLoaded(adcell => {
      const params = trackingParams ?? readParkedAdcellTrackingParams()
      adcell.Tracking.track(params?.bid, params?.adcref)
    })

    onError(error => {
      logError('ADCELL first-party tracking script loading failed', error)
    })

    const trackPaidSubscription = (subscription: AdcellPaidSubscription) => {
      if (!hasMarketingConsent.value) return

      if (!(trackingParams ?? readParkedAdcellTrackingParams())) return

      const { onLoaded: onConversionLoaded, onError: onConversionError } = useScript(
        {
          src: buildAdcellConversionUrl(subscription),
          type: 'text/javascript',
          async: true,
          crossorigin: false,
        },
        { trigger: 'client' }
      )

      onConversionLoaded(() => {
        clearParkedAdcellTrackingParams()
      })

      onConversionError(error => {
        logError('ADCELL conversion tracking script loading failed', error)
      })
    }

    return { provide: { adcell: { trackPaidSubscription } satisfies ProvidedAdcell } }
  },
})
